import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { groupService } from '../../services/group.service'
import { documentService } from '../../services/document.service'
import { userService } from '../../services/user.service'
import { toastService } from '../../services/toast.service'
import { Group, User } from '../../types'
import { InputText } from './InputText'

export interface SignerRow {
  id: string
  email: string
  name: string
}

type SignerSection = 'client' | 'company'

interface DocuSignSendDialogProps {
  show: boolean
  onHide: () => void
  documentId: number
  onSent?: () => void
}

const newSignerRow = (): SignerRow => ({
  id: `signer-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  email: '',
  name: '',
})

const DocuSignSendDialog: React.FC<DocuSignSendDialogProps> = ({
  show,
  onHide,
  documentId,
  onSent,
}) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [clientSigners, setClientSigners] = useState<SignerRow[]>(() => [newSignerRow()])
  const [companySigners, setCompanySigners] = useState<SignerRow[]>([])
  const [addToSection, setAddToSection] = useState<SignerSection>('company')
  const [consentUrl, setConsentUrl] = useState<string | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [groups, setGroups] = useState<Group[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [groupsLoading, setGroupsLoading] = useState(false)

  const activeUsersWithEmail = users.filter((u) => u.is_active && (u.email || '').trim().length > 0)

  useEffect(() => {
    if (!show) {
      setConsentUrl(null)
      setClientSigners([newSignerRow()])
      setCompanySigners([])
      setAddToSection('company')
      return
    }
    const loadUsers = async () => {
      setUsersLoading(true)
      try {
        const res = await userService.listAll({ page: 1, page_size: 10000, full_perms: true })
        setUsers(res.results || [])
      } catch {
        // ignore
      } finally {
        setUsersLoading(false)
      }
    }
    const loadGroups = async () => {
      setGroupsLoading(true)
      try {
        const res = await groupService.listAll({ page: 1, page_size: 1000 })
        setGroups(res.results || [])
      } catch {
        // ignore
      } finally {
        setGroupsLoading(false)
      }
    }
    loadUsers()
    loadGroups()
  }, [show])

  const addSigner = useCallback((section: SignerSection) => {
    if (section === 'client') {
      setClientSigners((prev) => [...prev, newSignerRow()])
    } else {
      setCompanySigners((prev) => (prev.length === 0 ? [newSignerRow()] : [...prev, newSignerRow()]))
    }
  }, [])

  const updateSigner = useCallback(
    (section: SignerSection, id: string, field: 'email' | 'name', value: string) => {
      if (section === 'client') {
        setClientSigners((prev) =>
          prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
        )
      } else {
        setCompanySigners((prev) =>
          prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
        )
      }
    },
    []
  )

  const removeSigner = useCallback((section: SignerSection, id: string) => {
    if (section === 'client') {
      setClientSigners((prev) => (prev.length <= 1 ? prev : prev.filter((s) => s.id !== id)))
    } else {
      setCompanySigners((prev) => prev.filter((s) => s.id !== id))
    }
  }, [])

  const addSignersToSection = useCallback(
    (section: SignerSection, newRows: SignerRow[]) => {
      const existingEmails = new Set([
        ...clientSigners.map((s) => s.email.trim().toLowerCase()),
        ...companySigners.map((s) => s.email.trim().toLowerCase()),
      ])
      const toAdd = newRows.filter((r) => r.email && !existingEmails.has(r.email.toLowerCase()))
      if (toAdd.length === 0) return
      if (section === 'client') {
        setClientSigners((prev) => [...prev, ...toAdd])
      } else {
        setCompanySigners((prev) => [...prev, ...toAdd])
      }
    },
    [clientSigners, companySigners]
  )

  const addSignersFromUsers = useCallback(
    (userIds: number[]) => {
      const toAdd = activeUsersWithEmail.filter((u) => userIds.includes(u.id))
      const newRows: SignerRow[] = toAdd.map((u) => ({
        id: `user-${u.id}-${Date.now()}`,
        email: (u.email || '').trim(),
        name: [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.username || '',
      }))
      addSignersToSection(addToSection, newRows)
    },
    [activeUsersWithEmail, addToSection, addSignersToSection]
  )

  const [groupUsersLoading, setGroupUsersLoading] = useState(false)

  const addSignersFromGroup = useCallback(
    async (groupId: number | null) => {
      if (!groupId) return
      setGroupUsersLoading(true)
      try {
        const usersInGroup = await groupService.getGroupUsers(groupId)
        const newRows: SignerRow[] = usersInGroup.map((u) => ({
          id: `group-${u.id}-${Date.now()}`,
          email: (u.email || '').trim(),
          name: [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.username || '',
        }))
        addSignersToSection(addToSection, newRows)
      } catch {
        toastService.showError(t('docusign.send.sendError'))
      } finally {
        setGroupUsersLoading(false)
      }
    },
    [addToSection, addSignersToSection, t]
  )

  const handleSend = async () => {
    const clientList = clientSigners
      .map((s) => ({ email: s.email.trim(), name: s.name.trim() || undefined }))
      .filter((s) => s.email)
    const companyList = companySigners
      .map((s) => ({ email: s.email.trim(), name: s.name.trim() || undefined }))
      .filter((s) => s.email)
    if (!clientList.length && !companyList.length) return
    setLoading(true)
    try {
      await documentService.docusignSend(documentId, {
        client_signers: clientList,
        company_signers: companyList,
      })
      setClientSigners([newSignerRow()])
      setCompanySigners([])
      onHide()
      onSent?.()
      toastService.showSuccess(t('docusign.send.envelopeSent'))
    } catch (e: any) {
      const data = e?.response?.data
      const detail = data?.detail
      const message =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.join(' ')
            : t('docusign.send.sendError')
      if (data?.consent_url) {
        setConsentUrl(data.consent_url)
        toastService.showError(message, e)
      } else {
        toastService.showError(message, e)
      }
    } finally {
      setLoading(false)
    }
  }

  const openConsentInNewTab = () => {
    if (consentUrl) window.open(consentUrl, '_blank', 'noopener,noreferrer')
  }

  const hasValidSigner =
    clientSigners.some((s) => (s.email || '').trim().length > 0) ||
    companySigners.some((s) => (s.email || '').trim().length > 0)

  type Option = { value: number; label: string }
  const userOptions: Option[] = activeUsersWithEmail.map((u) => ({
    value: u.id,
    label: `${u.username}${u.email ? ` (${u.email})` : ''}`,
  }))
  const groupOptions: Option[] = groups.map((g) => ({ value: g.id, label: g.name }))

  const renderSignerList = (section: SignerSection, signers: SignerRow[]) => (
    <>
      {signers.map((signer) => (
        <div key={signer.id} className="d-flex flex-wrap gap-2 align-items-start mb-2">
          <div className="flex-grow-1" style={{ minWidth: '140px' }}>
            <InputText
              type="email"
              value={signer.email}
              onChange={(value) => updateSigner(section, signer.id, 'email', value)}
              placeholder={t('enter_signer_email')}
            />
          </div>
          <div className="flex-grow-1" style={{ minWidth: '120px' }}>
            <InputText
              value={signer.name}
              onChange={(value) => updateSigner(section, signer.id, 'name', value)}
              placeholder={t('enter_signer_name_optional')}
            />
          </div>
          <div className="d-flex align-items-center pt-2">
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => removeSigner(section, signer.id)}
              disabled={section === 'client' && signers.length <= 1}
              title={t('delete')}
              aria-label={t('delete')}
            >
              <i className="bi bi-trash" aria-hidden />
            </Button>
          </div>
        </div>
      ))}
    </>
  )

  return (
    <Modal show={show} onHide={onHide} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t('docusign.send.forSignature')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {consentUrl && (
          <Alert variant="warning" className="mb-3">
            <Alert.Heading>{t('docusign.send.consentRequired')}</Alert.Heading>
            <p className="mb-2">{t('docusign.send.consentRequiredHelp')}</p>
            <Button variant="primary" size="sm" onClick={openConsentInNewTab}>
              {t('docusign.send.openConsentPage')}
            </Button>
          </Alert>
        )}

        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <Form.Label className="mb-0 fw-semibold">{t('docusign.signers.clients')}</Form.Label>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => addSigner('client')}
              title={t('docusign.signers.addSigner')}
            >
              <i className="bi bi-plus-lg" aria-hidden />
              <span className="ms-1">{t('docusign.signers.addSigner')}</span>
            </Button>
          </div>
          {renderSignerList('client', clientSigners)}
        </div>

        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <Form.Label className="mb-0 fw-semibold">{t('docusign.signers.company')}</Form.Label>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => addSigner('company')}
              title={t('docusign.signers.addSigner')}
            >
              <i className="bi bi-plus-lg" aria-hidden />
              <span className="ms-1">{t('docusign.signers.addSigner')}</span>
            </Button>
          </div>
          {companySigners.length === 0 ? (
            <p className="text-muted small mb-0">{t('docusign.signers.companyOptional')}</p>
          ) : (
            renderSignerList('company', companySigners)
          )}
        </div>

        <div className="mb-2">
          <Form.Label className="fw-semibold">{t('docusign.signers.addToSection')}</Form.Label>
          <Form.Select
            value={addToSection}
            onChange={(e) => setAddToSection(e.target.value as SignerSection)}
            size="sm"
            style={{ maxWidth: '280px' }}
          >
            <option value="client">{t('docusign.signers.clients')}</option>
            <option value="company">{t('docusign.signers.company')}</option>
          </Form.Select>
        </div>
        <div className="mb-3">
          <Form.Label className="fw-semibold">{t('docusign.signers.addDmsUsers')}</Form.Label>
          <Select<Option, true>
            isMulti
            options={userOptions}
            value={[] as Option[]}
            onChange={(opts) => {
              const ids = opts ? opts.map((o) => o.value) : []
              addSignersFromUsers(ids)
            }}
            placeholder={t('select_users')}
            isClearable
            isLoading={usersLoading}
            isDisabled={usersLoading}
            className="react-select-container"
            classNamePrefix="react-select"
            noOptionsMessage={() => (usersLoading ? t('loading') : t('no_results'))}
          />
        </div>
        <div className="mb-3">
          <Form.Label className="fw-semibold">{t('docusign.signers.addGroup')}</Form.Label>
          <Select<Option>
            options={groupOptions}
            value={null}
            onChange={(opt) => {
              const id = opt?.value ?? null
              if (id != null) void addSignersFromGroup(id)
            }}
            placeholder={t('select_groups')}
            isClearable
            isLoading={groupsLoading || groupUsersLoading}
            isDisabled={groupsLoading || groupUsersLoading}
            className="react-select-container"
            classNamePrefix="react-select"
            noOptionsMessage={() => (groupsLoading ? t('loading') : t('no_results'))}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          {t('cancel')}
        </Button>
        <Button variant="primary" onClick={handleSend} disabled={loading || !hasValidSigner}>
          {loading && <Spinner animation="border" size="sm" className="me-2" />}
          {t('send_for_signature')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default DocuSignSendDialog
